//#region [IMPORT]

// === THIRD-PARTY ===
import React, { useState, useContext, useMemo } from 'react';

import { useTheme } from '@mui/material/styles';
import {
  Autocomplete,
  createFilterOptions,
  TextField,
} from '@mui/material';

// === CONSTANTS ===
import { SUCCESS_CODE } from 'constants/general';

// === COMPONENTS ===
import { FieldPanelContext } from '../../../FieldPanelContext/FieldPanelContext';
import { validate } from './validation/index';

// === API ===
import { requestPreviousFieldFocus, updateField } from "api/prokey/prokey.websocket";
import { getHexAlpha } from 'components/styles.utils';

//#endregion

export { validate };

const filter = createFilterOptions();
//-----------------------------------------------------------------
export function Pagetype( props ) {
  //-----------------------------------------------------------------

  //#region [VALUES]

  const { field, value, setMessage } = props;
  
  const theme = useTheme();

  const {
    pagetypes,
  } = useContext( FieldPanelContext );

  const options  = useMemo( createOptions, [pagetypes] );
  const doc_identified = useMemo( ()=>pagetypes.find( x => getNumCode( x.numcode ) === getNumCode( value ) ), [ pagetypes, value ] );

  const [ document  , setDocument  ] = useState( doc_identified );
  const [ input     , setInput     ] = useState( document?.label || '' );
  const [ highlight , setHighlight ] = useState( null );
  const [ is_open   , setIsOpen    ] = useState( false );

  field.validationInfo.acceptableValues = pagetypes ? Object.values(pagetypes).map( x => x.numcode ) : [];

  //#endregion
  
  //#region [STYLES]

  const pt_style = {
    ...props.style,
    width: '100%',
  }
  
  const SELECT = {
    sx               : pt_style ,
    id               : props.id ,
    options          : options  ,
    value            : document ,
    inputValue       : input    ,
    size             : 'small'  ,
    forcePopupIcon   : false    ,
    autoHighlight    : true     ,
    selectOnFocus    : false    ,
    selected         : props.has_focus,
    disabled         : field.isReadOnly,
    disablePortal    : true,
    disableClearable : true,
    open             : is_open,
    onOpen      : (event) => {
      if( event?.type !== 'mousedown' ) setIsOpen(true);
    },
    onClose     : () => setIsOpen(false),
    onChange    : onChange,
    onFocus     : props.onFocus,
    onBlur      : props.onBlur,
    renderInput : (params) => <TextField {...params} 
      inputRef={props.inputRef}
      tabIndex={0}
      variant='standard' // Use a variant with no FormControl border
      InputProps={{
        ...params.InputProps,
        height: '20px',
        border : '0px',
        disableUnderline: true,
      }}
      inputProps={{
        ...params.inputProps,
        style: {
          ...params.inputProps.style,
          color: theme.palette.text.primary,
          fontWeight: 600,
          opacity: 1,
          WebkitTextFillColor: theme.palette.text.primary,
        },
      }}
      sx={{
        alignItems: 'center',
        paddingTop: '4px',
        '& .MuiFormControl-root': {
          display: 'unset',
        },
      }}
    />,
    renderOption: (props, option) => {
      const is_highlighted = highlight?.numcode === option.numcode;
      const color_bg   = is_highlighted ? theme.palette.primary.main         : undefined;
      const color_text = is_highlighted ? theme.palette.primary.contrastText : undefined;

      const {key, ...rest} = props;
      return (
        <li
          key={key}
          {...rest}
          style={{
            backgroundColor : color_bg  ,
            color           : color_text,
          }}
        >
          {option.label}
        </li>
      );
    },
    slotProps: {
      paper: {
        sx: {
          borderRadius : '0 0 5px 5px' ,
          borderStyle  : 'solid'       ,
          borderTop    : '0',
          borderColor  : theme.palette.primary[theme.palette.mode],
          boxShadow    : 3, // Dropdown shadow
        },
      },
    },
    ListboxProps : {
      onMouseMove: (event) => event.preventDefault()
    },
    onInputChange : (event, new_value, reason) => {
      const is_input = reason === 'input';
      const is_reset = reason === 'reset';
      const is_event = event?.type === "click" || event?.type === "keydown";
      if (is_reset && is_event ) {
        setInput(new_value);
      }

      if( is_input ) {
        setInput(new_value);
      }

      if ( is_input && new_value !== document?.label) {
        setDocument(null);
      }

    },
    onHighlightChange : (event, option, reason) => {
      if( reason !== 'mouse' ) {
        setHighlight(option);
      }
    },
    onKeyDown : onKeyDown,
    getOptionLabel : (option) => option.label,
    isOptionEqualToValue : (option, value) => {
      if (typeof value === 'string') {
        return getNumCode( option.numcode ) === getNumCode( value );
      }
      return getNumCode( option.numcode ) === getNumCode( value.numcode );
    },
    groupBy: (option) => option.group,
    filterOptions : (options, params) => {
      const filtered = filter(options, params);
      const is_empty = params.inputValue === '';

      if( is_empty ) return filtered;

      const exact_match_index = filtered.findIndex(
        (option) => {
          const is_exact_numcode = getNumCode(option.numcode ) === getNumCode( params.inputValue.toUpperCase() );
          const is_eaxct_name    = option.name.toUpperCase() === params.inputValue.toUpperCase();
          return is_exact_numcode || is_eaxct_name;
        }
      );

      // If an exact match is found, move it to the beginning of the list
      if (exact_match_index > -1) {
        const exact_match = filtered.splice(exact_match_index, 1)[0];
        filtered.unshift(exact_match);
      }

      return filtered;
    },
  };

  //#endregion

  //#region [METHOD]

  //-----------------------------------------------------------------
  function createOptions() {
    //-----------------------------------------------------------------
    let all          = [];
    let identified   = [];
    let alternatives = [];

    pagetypes.forEach( x => {
      const is_identified = getNumCode(field.DisplayInfo.IdentifiedValue) === getNumCode(x.numcode);
      const is_alternativ = !!field.DisplayInfo.AltContent.find( num => getNumCode(num) === getNumCode(x.numcode) );
      const doc_option = { ...x };
      
      if(false) {}
      else if( is_identified ) {
        doc_option.group = 'Identified';
        identified.push( doc_option );
      }
      else if( is_alternativ ) {
        doc_option.group = 'Likely Alternatives';
        alternatives.push( doc_option );
      }
      else {
        doc_option.group = 'All';
        all.push( doc_option );
      }
    });

    const result = [ ...identified, ...alternatives, ...all ];
    return result;
  }

  //-----------------------------------------------------------------
  function getNumCode(doctype) {
    //-----------------------------------------------------------------
    if (!doctype) return '';

    let numcode = doctype;
         if (numcode[0] === 'D') ;
    else if (numcode[0] === 'F') numcode = 'F';
    else if (numcode[0] === 'B') numcode = 'B';
    else                         numcode = 'D' + numcode;

    return numcode;
  }

  //#endregion

  //#region [EVENTS]

  //-----------------------------------------------------------------
  function onChange( event, new_value ) {
    //-----------------------------------------------------------------
    if( !new_value ) return;

    setDocument(new_value);
    setInput(new_value.label);

    const result = validate(new_value.numcode, field.validationInfo);
    if( result.status === SUCCESS_CODE ) {
      updateField(event?.trigger, props.field.id, props.field.Name, { Content: result.content } );
    }
    setMessage( result.message );
  }

  //-----------------------------------------------------------------
  function onKeyDown( event ) {
    //-----------------------------------------------------------------
    try {
      const is_action_prev = event.key === 'Tab' && event.shiftKey ;
      if( is_action_prev ) {
        event.preventDefault();
        event.stopPropagation();
        requestPreviousFieldFocus( props.id );
        return;
      }
      
      let trigger = ''
      const is_action_force = event.key === 'Enter' &&  event.ctrlKey;
      const is_action_enter = event.key === 'Enter' && !event.ctrlKey;
      const is_action_tab   = event.key === 'Tab'   && !event.ctrlKey;
      if( is_action_force ) {
        trigger = 'FORCE';
      }
      if( is_action_enter ) {
        trigger = 'ENTER';
      }
      if( is_action_tab ) {
        trigger = 'TAB';
      }

      const is_submit = is_action_force || is_action_enter || is_action_tab;
      submit_actions:
      if( is_submit && !is_open ) {
        event.preventDefault();
        event.stopPropagation();

        const is_filter = input !== document?.label;
        const content   = is_filter ? input : document.numcode;
        const result    = validate( content, field.validationInfo );

        if( result.status === SUCCESS_CODE ) {
          updateField( trigger, props.field.id, props.field.Name, { Content: result.content } );
        }

        setMessage( result.message );
      }
      else if( is_submit && is_open ) {
        event.preventDefault();
        event.stopPropagation();
        event.defaultMuiPrevented = true;

        if( !highlight ) {
          break submit_actions;
        }

        setDocument( highlight );
        setInput( highlight.label );

        const result = validate(highlight.numcode, field.validationInfo);
        if( result.status === SUCCESS_CODE ) {
          updateField( trigger, props.field.id, props.field.Name, { Content: result.content } );
        }
        setMessage( result.message );
      }
    } catch (error) {
      console.warn(error);
    }
  }

  //#endregion

  //#region [RENDER]

  //-----------------------------------------------------------------
  function display() {
    //-----------------------------------------------------------------
    return (
      <Autocomplete { ...SELECT } />
    );
  }

  //#endregion

  return display();
}
